// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {Dialog, MultiselectFilterList} from '@deere/isg.component-library';
import {replaceTranslationNames} from 'Src/ui-core/utils/translation-utils';
import {
    selectOperator,
    setSelectedJobs
} from 'Src/ui-core/store/actions/setup-workboard';
import {
    getSelectedJobs,
    getSelectedOperators
} from 'OnLabor/workboard/utils/setup-workboard';
import dialogTypes from 'Ui/components/common/dialog-types';
import {closeDialog} from 'Src/ui-core/store/actions/dialogs';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {getOvertimeThresholds} from 'OnLabor/workboard/utils/overtime-status';
import {
    getGroupedFormattedOperators,
    getOperatorFormattedName
} from 'OnLabor/workboard/utils/operator-utils';

function handleAddJobs(
    jobs,
    itemsToAdd,
    selectedJobs,
    handleSelectJob,
    addToast,
    dismissDialog,
    translations
) {
    if (itemsToAdd?.length === 0) {
        dismissDialog();
        return;
    }

    const jobsToBeAdded = getSelectedJobs(jobs, itemsToAdd);
    const jobsToBeAddedNames = jobsToBeAdded.map((job) => job.title).join(', ');

    try {
        handleSelectJob({
            jobs: [...selectedJobs, ...jobsToBeAdded]
        });
        addToast({
            title: translations.SUCCESS,
            message: replaceTranslationNames(translations.ONLINK_ADD_SUCCESS, {
                '0': jobsToBeAddedNames
            }),
            persist: false,
            type: TOAST_TYPE.SUCCESS
        });
    } catch {
        addToast({
            title: translations.ONLINK_ADD_JOB_ERROR,
            message: replaceTranslationNames(
                translations.ONLINK_ADD_ASSIGNMENT_ERROR_TOAST,
                {
                    '0': jobsToBeAddedNames
                }
            ),
            persist: false,
            type: TOAST_TYPE.ERROR
        });
    } finally {
        dismissDialog();
    }
}

function handleAddOperators(
    operators,
    itemsToAdd,
    selectedOperators,
    handleSelectOperator,
    addToast,
    dismissDialog,
    translations
) {
    if (itemsToAdd?.length === 0) {
        dismissDialog();
        return;
    }

    const operatorsToBeAdded = getSelectedOperators(operators, itemsToAdd);
    const operatorsToBeAddedNames = operatorsToBeAdded
        .map((operator) => getOperatorFormattedName(operator))
        .join(', ');

    try {
        handleSelectOperator({
            newOperators: [...selectedOperators, ...operatorsToBeAdded]
        });
        addToast({
            title: translations.SUCCESS,
            message: replaceTranslationNames(translations.ONLINK_ADD_SUCCESS, {
                '0': operatorsToBeAddedNames
            }),
            persist: false,
            type: TOAST_TYPE.SUCCESS
        });
    } catch {
        addToast({
            title: translations.ONLINK_ADD_OPERATOR_ERROR,
            message: replaceTranslationNames(
                translations.ONLINK_ADD_ASSIGNMENT_ERROR_TOAST,
                {
                    '0': operatorsToBeAddedNames
                }
            ),
            persist: false,
            type: TOAST_TYPE.ERROR
        });
    } finally {
        dismissDialog();
    }
}

function getSelectAllConfirmationTitle(addUser, pendingSelectedIds, translations) {
    if (pendingSelectedIds.length === 0) {
        return null;
    }
    const itemType = addUser ? translations.OPERATORS : translations.JOBS;

    return replaceTranslationNames(translations.ONLINK_SELECT_ALL_CONFIRMATION, {
        '0': String(pendingSelectedIds.length),
        '1': itemType
    });
}

function getSelectAllFooterActions(translations, setItemsToAdd, setPendingSelectedIds, pendingSelectedIds) {
    return [
        {
            title: translations.CANCEL,
            variant: 'secondary',
            onClick: () => setPendingSelectedIds([])
        },
        {
            title: translations.confirm,
            variant: 'primary',
            onClick: () => {
                setItemsToAdd(pendingSelectedIds);
                setPendingSelectedIds([]);
            }
        }
    ];
}

function AddAssignmentsDialog(props) {
    const {
        addUser,
        handleAddedItems,
        jobs,
        selectedJobs,
        handleSelectJob,
        handleSelectOperator,
        membership,
        operators,
        selectedOperators,
        closeAddAssignmentsDialog,
        translations
    } = props;

    const [itemsToAdd, setItemsToAdd] = React.useState([]);
    const [pendingSelectedIds, setPendingSelectedIds] = React.useState([]);
    const containerRef = React.useRef(null);

    const selectedJobIds = React.useMemo(() => {
        return selectedJobs.map((job) => job.id);
    }, [selectedJobs]);

    const selectedOperatorsIds = React.useMemo(() => {
        return selectedOperators.map((operator) => operator.id);
    }, [selectedOperators]);

    const jobItems = React.useMemo(() => {
        return jobs.map((job) =>
            selectedJobIds.includes(job.id) ?
                {
                    ...job,
                    disabled: true
                } :
                job
        );
    }, [jobs, selectedJobs]);

    const {
        overtimeReachedThreshold, overtimeWarningThreshold
    } =
        getOvertimeThresholds(
            membership.properties.workboard_weekly_work_hours,
            membership.properties.workboard_employee_daily_hours_limit
        );

    const operatorItems = React.useMemo(() => {
        const {groupedOperators: groups} = getGroupedFormattedOperators(
            {
                operators,
                overtimeReachedThreshold,
                overtimeWarningThreshold,
                showIcon: true,
                translations
            },
            true
        );

        const updatedGroups = groups.map((group) => ({
            ...group,
            items: group.items.map((operator) => ({
                ...operator,
                disabled: selectedOperatorsIds.includes(operator.id)
            }))
        }));

        return {
            groups: updatedGroups,
            items: [],
            groupOptions: {
                collapseAll: true
            }
        };
    }, [
        operators,
        selectedOperators,
        overtimeReachedThreshold,
        overtimeWarningThreshold
    ]);

    const operatorItemsCount = React.useMemo(() => {
        return operatorItems.groups.reduce(
            (sum, g) => sum + g.items.length,
            0
        );
    }, [operatorItems]);

    const jobItemsCount = React.useMemo(() => {
        return jobItems.length;
    }, [jobItems]);

    const {addToast} = useToast();

    function onChange(newIds) {
        const selectableCount = addUser ?
            operatorItemsCount - selectedOperatorsIds.length :
            jobItemsCount - selectedJobIds.length;

        const isSelectingAll =
            selectableCount > 1 &&
            newIds.length === selectableCount &&
            itemsToAdd.length < selectableCount - 1;

        if (isSelectingAll) {
            setPendingSelectedIds(newIds);
            return;
        }

        setItemsToAdd(newIds);
    }

    const dismissDialog = () => {
        closeAddAssignmentsDialog();
        if (handleAddedItems) {
            handleAddedItems(itemsToAdd);
        }
        setItemsToAdd([]);
    };

    const footerActions = [
        {
            title: translations.CANCEL,
            variant: 'secondary',
            onClick: closeAddAssignmentsDialog
        },
        {
            title: translations.save,
            variant: 'primary',
            onClick: (e) => {
                e.preventDefault();
                addUser ?
                    handleAddOperators(
                        operators,
                        itemsToAdd,
                        selectedOperators,
                        handleSelectOperator,
                        addToast,
                        dismissDialog,
                        translations
                    ) :
                    handleAddJobs(
                        jobs,
                        itemsToAdd,
                        selectedJobs,
                        handleSelectJob,
                        addToast,
                        dismissDialog,
                        translations
                    );
            }
        }
    ];

    return (
        <Dialog
            closeHandler={dismissDialog}
            contentRef={containerRef}
            footerActions={
                pendingSelectedIds.length > 0 ? getSelectAllFooterActions(translations, setItemsToAdd, setPendingSelectedIds, pendingSelectedIds) : footerActions
            }
            footerMessage={getSelectAllConfirmationTitle(addUser, pendingSelectedIds, translations)
            }
            open={true}
            title={replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                '0': addUser ? translations.OPERATORS : translations.JOBS
            })}
            width='sm'
        >
            <MultiselectFilterList
                containerRef={containerRef}
                disableSorting
                emptyListLabel={translations.ONLINK_WORKBOARD_NO_RESULTS}
                enableVirtualization
                groupOptions={addUser ? operatorItems.groupOptions : {}}
                groups={addUser ? operatorItems.groups : []}
                items={addUser ? operatorItems.items : jobItems}
                noItemMatchLabel={translations.NO_RESULTS_FOUND}
                onChange={(selectedIds) => onChange(selectedIds)}
                selectAllItemLabel={translations.SELECT_ALL}
                selectedIds={itemsToAdd}
            />
        </Dialog>
    );
}

AddAssignmentsDialog.propTypes = {
    addUser: PropTypes.bool,
    closeAddAssignmentsDialog: PropTypes.func,
    handleAddedItems: PropTypes.func,
    handleSelectJob: PropTypes.func,
    handleSelectOperator: PropTypes.func,
    jobs: PropTypes.arrayOf(PropTypes.object),
    jobsToRemove: PropTypes.array,
    membership: PropTypes.membership,
    operators: PropTypes.arrayOf(PropTypes.object),
    operatorsToRemove: PropTypes.array,
    selectedJobs: PropTypes.arrayOf(PropTypes.object),
    selectedOperators: PropTypes.arrayOf(PropTypes.object),
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        jobsToRemove: state.setupWorkboard.jobsToRemove,
        operatorsToRemove: state.setupWorkboard.operatorsToRemove,
        selectedJobs: state.setupWorkboard.jobs.selectedJobs,
        jobs: state.setupWorkboard.jobs.jobs,
        operators: state.setupWorkboard.operators.teamMembers,
        selectedOperators: state.setupWorkboard.operators.selected,
        membership: state.membership,
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        handleSelectJob(props) {
            dispatch(setSelectedJobs(props));
        },
        handleSelectOperator(props) {
            dispatch(selectOperator(props));
        },
        closeAddAssignmentsDialog() {
            dispatch(closeDialog(dialogTypes.ADD_ASSIGNMENTS_DIALOG));
        }
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AddAssignmentsDialog);
